import { Box, CircularProgress, Stack } from "@mui/material";
import React, { useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from "recharts";

import { ApiError } from "api/types";
import { Typography } from "new-components/typography/Typography";

import { AccountInfoData } from "./AccountInfo";
import { MeteringByAccountData } from "./MeteringInfo";

interface MeteringBarChartProps {
  data: MeteringByAccountData[];
  loadingInfo: boolean;
  accountMetricsError: ApiError<unknown> | null;
}

interface ContentProps {
  date: string;
  imageQueries: number;
  escalations: number;
}

function CustomTooltip({ position, content, onMouseEnter, onMouseLeave }: any) {
  const { x, y } = position || {};
  const tooltipWidth = 256; // Width of the tooltip
  const tooltipHeight = 150; // Approximate height of the tooltip
  const padding = 5; // Padding from edges
  const margin = 100; // Margin from edges

  // Calculate if tooltip would go off screen
  const isNearRightEdge = x + tooltipWidth + padding + margin > window.innerWidth;
  const isNearBottomEdge = y + tooltipHeight + padding + margin > window.innerHeight;

  // Adjust position if near edges
  const adjustedX = isNearRightEdge ? x - tooltipWidth - padding : x + padding;
  const adjustedY = isNearBottomEdge ? y - tooltipHeight - padding : y + padding;

  return (
    <Stack
      sx={{
        backgroundColor: "white",
        color: "black",
        boxShadow: "4px 4px 20px 0px #00000040",
        padding: "10px 20px",
        borderRadius: "5px",
        position: "fixed",
        zIndex: 1,
        left: adjustedX,
        top: adjustedY,
        gap: "10px",
        opacity: 0,
        transform: "scale(0.95)",
        animation: "tooltipFadeIn 0.1s ease-out forwards",
        "@keyframes tooltipFadeIn": {
          "0%": {
            opacity: 0,
            transform: "scale(0.95)",
          },
          "100%": {
            opacity: 1,
            transform: "scale(1)",
          },
        },
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {content}
    </Stack>
  );
}

// Small component to render an error state
function RenderErrorState() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
      <Box sx={{ textAlign: "center" }}>
        <p>Sorry, we encountered an error while loading the chart data.</p>
      </Box>
    </Box>
  );
}

function ContentItem(props: { color: string; label: string; value: number }) {
  const { color, label, value } = props;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "5px", justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <Box sx={{ width: "16px", height: "16px", backgroundColor: color }} />
        <Typography>{label}</Typography>
      </Box>
      <Typography sx={{ fontWeight: 500 }}>{value}</Typography>
    </Box>
  );
}

function Content({ date, imageQueries, escalations }: ContentProps) {
  return (
    <>
      <Typography sx={{ fontWeight: "600" }}>{date}</Typography>
      <ContentItem color="#529FE8" label="Cloud queries" value={imageQueries} />
      <ContentItem color="#0450BA" label="Groundlight Cloud labeler answers" value={escalations} />
    </>
  );
}

function EmptyChartContainer(props: { children: React.ReactNode }) {
  const { children } = props;

  return (
    <Box
      sx={{
        position: "absolute",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "white",
        opacity: 0.75,
        zIndex: 10,
      }}
    >
      {children}
    </Box>
  );
}

function ChartLabelMarker(props: { children: React.ReactNode; value: number; color: string }) {
  const { children, value, color } = props;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        bgcolor: color,
        borderRadius: "5px",
        padding: "2px 10px",
        color: "white",
        gap: "10px",
      }}
    >
      <Typography sx={{ textTransform: "uppercase", fontSize: "12px", lineHeight: "14px", fontWeight: "500" }}>
        {children}
      </Typography>
      <Typography sx={{ fontWeight: "bold", fontSize: "24px" }}>{value}</Typography>
    </Box>
  );
}

// Sample data for testing the MeteringBarChart component

export function MeteringBarChart(props: MeteringBarChartProps) {
  // Define Custom Tooltip
  interface Tooltip {
    show: boolean;
    position: { x?: number; y?: number };
    content?: React.ReactNode;
  }
  const [tooltip, setTooltip] = useState({ show: false, position: {} } as Tooltip);
  const { data, loadingInfo, accountMetricsError } = props;

  const { totalImageQueries, totalEscalations } = (() =>
    data.reduce(
      (acc, curr) => ({
        totalImageQueries: acc.totalImageQueries + curr.image_queries,
        totalEscalations: acc.totalEscalations + curr.escalations,
      }),
      {
        totalImageQueries: 0,
        totalEscalations: 0,
      }
    ))();

  let tooltipTimeout: any;
  const showTooltip = (item: any, i: any, e: any) => {
    clearTimeout(tooltipTimeout);
    setTooltip({
      show: true,
      position: { x: e.clientX, y: e.clientY },
      content: <Content date={item.interval_start} imageQueries={item.image_queries} escalations={item.escalations} />,
    });
  };

  const hideTooltip = (e: any) => {
    tooltipTimeout = setTimeout(() => setTooltip({ show: false, ...e }), 200);
  };

  return (
    <Stack sx={{ alignItems: "center", justifyContent: "center", position: "relative", gap: "10px" }}>
      <AccountInfoData text="Cloud activity" />
      {loadingInfo && (
        <EmptyChartContainer>
          <CircularProgress data-testid="loading-circular-progress" />
        </EmptyChartContainer>
      )}
      {accountMetricsError && (
        <EmptyChartContainer>
          <RenderErrorState />
        </EmptyChartContainer>
      )}
      <Box sx={{ width: "100%" }}>
        <Box sx={{ height: "170px" }}>
          <ResponsiveContainer>
            <BarChart data={loadingInfo ? [] : data}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              {/* Groundlight cloud labeler answers */}
              <Bar
                onMouseEnter={showTooltip}
                onMouseLeave={hideTooltip}
                barSize={17}
                dataKey="escalations"
                stackId="a"
                fill="#0450BA"
              />
              {/* Cloud queries */}
              <Bar
                onMouseEnter={showTooltip}
                onMouseLeave={hideTooltip}
                barSize={17}
                dataKey="graphImageQueries"
                stackId="a"
                fill="#529FE8"
              />
              <XAxis
                dataKey="interval_start"
                tick={{
                  fill: "#919599",
                  style: {
                    fontSize: 10,
                    fontWeight: "500",
                  },
                }}
              />
              <YAxis
                tick={{
                  fill: "#919599",
                  style: {
                    fontSize: 10,
                    fontWeight: "500",
                  },
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px" }}>
          <ChartLabelMarker value={totalImageQueries} color="#529FE8">
            Cloud <br /> queries
          </ChartLabelMarker>
          <ChartLabelMarker value={totalEscalations} color="#0450BA">
            Groundlight <br /> Cloud labeler answers
          </ChartLabelMarker>
        </Box>
      </Box>

      {tooltip?.show && (
        <CustomTooltip
          onMouseEnter={() => {
            clearTimeout(tooltipTimeout);
          }}
          onMouseLeave={() => {
            setTooltip({ show: false, position: {} });
            clearTimeout(tooltipTimeout);
          }}
          {...tooltip}
        />
      )}
    </Stack>
  );
}
